/**
 * What a web maintenance package gives a watched site, for the screens: the
 * package's name, and the lines the package card and the add dialog list.
 * See docs/saidiseire-paketid.md.
 *
 * React-free, so the rules are tested without mounting anything. Every line is
 * an i18n key under `monitoring.policy` with its values.
 */
import type { MonitorPlanView, MonitorPolicyView } from "./monitoring";

/** A package's name in the interface language, else Estonian, else English, else its code. */
export function planName(plan: Pick<MonitorPlanView, "code" | "name">, locale: string): string {
  return plan.name[locale] ?? plan.name.et ?? plan.name.en ?? plan.code;
}

/**
 * The name for a site's package code. A code whose package is gone shows as
 * the bare code, so nobody mistakes the site for one without a package. Null
 * for no package.
 */
export function planLabel(plans: readonly MonitorPlanView[] | null, code: string | null, locale: string): string | null {
  if (!code) return null;
  const plan = plans?.find((p) => p.code === code);
  return plan ? planName(plan, locale) : code;
}

/** Whether a site's package code names a package that no longer exists. Unknown while the list loads. */
export function planGone(plans: readonly MonitorPlanView[] | null, code: string | null): boolean {
  return Boolean(code && plans && !plans.some((p) => p.code === code));
}

export interface PolicyLine {
  /** Under `monitoring.policy`. `monthly` is the monthly report, not the WordPress `reporter`. */
  key: "every" | "cert" | "domain" | "speed" | "reporter" | "security" | "backupBefore" | "backupDaily" | "dbCleanup" | "monthly";
  vars?: { minutes: number };
}

/**
 * The switches a package can hold, in the catalog's order (the package
 * editor lists them the same way). `paid` marks the ones a site without a
 * package never gets (docs/saidiseire-kiirus-turve-raport.md, and the three
 * backup and database checks of docs/saidiseire-varundus.md).
 */
const SWITCHES: readonly { key: Exclude<PolicyLine["key"], "every">; on: (p: MonitorPolicyView) => boolean; paid: boolean }[] = [
  { key: "cert", on: (p) => p.certExpiry, paid: false },
  { key: "domain", on: (p) => p.domainExpiry, paid: false },
  { key: "speed", on: (p) => p.speedTest, paid: true },
  { key: "reporter", on: (p) => p.wpReporter, paid: false },
  { key: "security", on: (p) => p.securityScan, paid: true },
  { key: "backupBefore", on: (p) => p.backupBeforeUpdate, paid: true },
  { key: "backupDaily", on: (p) => p.backupDaily, paid: true },
  { key: "dbCleanup", on: (p) => p.dbCleanup, paid: true },
  { key: "monthly", on: (p) => p.monthlyReport, paid: true },
];

/** What the engine does for the site, in the order the screens list it. The check itself always runs. */
export function policyIncludes(policy: MonitorPolicyView): PolicyLine[] {
  const out: PolicyLine[] = [{ key: "every", vars: { minutes: policy.checkMinutes } }];
  for (const s of SWITCHES) if (s.on(policy)) out.push({ key: s.key });
  return out;
}

/**
 * What the package switches off for the site. A site without a package is
 * not told that "the package" leaves the paid features out: it has no
 * package, and the card already says so.
 */
export function policyExcludes(policy: MonitorPolicyView): PolicyLine[] {
  return SWITCHES.filter((s) => !s.on(policy) && !(s.paid && policy.source === "default")).map((s) => ({ key: s.key }));
}
